import { Box, Grid, Typography } from '@mui/material';
import {
  DynamicBadge,
  proAccountThemeCoreUI,
  type IStylesOverrides,
} from '@orcha/micro-library-core-ui';
import type { ImageProps } from 'next/image';

type ThresholdBannerStyleOverrides = IStylesOverrides & {
  textColor?: string;
  bannerPadding?: number;
  bannerRadius?: number;
  badgeImageWidth?: number;
  badgeImageHeight?: number;
  hasBoxShadow?: boolean;
};

type BannerContainerProps = {
  children: React.ReactNode;
  styleOverrides?: ThresholdBannerStyleOverrides;
  backgroundUrl?: string;
};

const BannerContainer = ({
  children,
  styleOverrides,
  backgroundUrl = '',
}: BannerContainerProps) => {
  return (
    <Box
      sx={{
        backgroundColor:
          styleOverrides?.backgroundColour ??
          proAccountThemeCoreUI.palette.background.default,
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '50px',
        position: 'relative',
        padding: styleOverrides?.bannerPadding ?? '1.3rem',
        borderRadius: styleOverrides?.bannerRadius ?? '10px',
        boxShadow: styleOverrides?.hasBoxShadow
          ? 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;'
          : undefined,
      }}
    >
      {children}
    </Box>
  );
};

export type ThresholdBannerConfiguration = {
  header: string;
  subheader: string;
  imageUrl: string;
  backgroundUrl: string;
  minValue: number;
  maxValue: number;
  styleOverrides?: ThresholdBannerStyleOverrides;
};

export type ThresholdBannerProps = {
  value: number;
  thresholds: ThresholdBannerConfiguration[];
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  testId?: string;
};

/**
 * ThresholdBanner component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {number} value - The value to check against the thresholds
 * @param {Threshold[]} thresholds - The thresholds to display in the banner
 * @param {React.ComponentType<ImageProps>} FlexibleImageComponent - The component to use for the image
 * @param {string} testId - The test id to use for the banner
 */
export const ThresholdBanner = ({
  value,
  thresholds,
  FlexibleImageComponent,
  testId = 'threshold-banner',
}: ThresholdBannerProps) => {
  const valueToUse = Math.round(value ?? 0);

  const matchedThreshold = thresholds?.find(
    (threshold) =>
      valueToUse >= threshold?.minValue && valueToUse <= threshold?.maxValue
  );

  if (!matchedThreshold) {
    return null;
  }

  const {
    header,
    subheader,
    imageUrl,
    backgroundUrl,
    minValue,
    maxValue,
    styleOverrides,
  } = matchedThreshold ?? {};

  if (!imageUrl) {
    return null;
  }

  return (
    <BannerContainer
      data-testid={testId}
      styleOverrides={styleOverrides}
      backgroundUrl={backgroundUrl}
    >
      <Grid item container direction="row" spacing={2}>
        <Grid item>
          <Box>
            <DynamicBadge
              value={value}
              thresholds={[
                { imageUrl, minValue: minValue ?? 0, maxValue: maxValue ?? 0 },
              ]}
              FlexibleImageComponent={FlexibleImageComponent}
              translatedText={{
                tooltipText: '',
              }}
              width={styleOverrides?.badgeImageWidth ?? 50}
              height={styleOverrides?.badgeImageHeight ?? 50}
            />
          </Box>
        </Grid>

        <Grid item>
          <Grid item container direction="column">
            <Grid item>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color:
                    styleOverrides?.textColor ??
                    proAccountThemeCoreUI.palette.text.primary,
                  marginBlockStart: 0,
                  marginBlockEnd: 0,
                  textOverflow: 'ellipsis',
                }}
                variant="h5"
              >
                {header ?? ''}
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color:
                    styleOverrides?.textColor ??
                    proAccountThemeCoreUI.palette.text.primary,
                  marginBlockStart: 0,
                  marginBlockEnd: 0,
                  textOverflow: 'ellipsis',
                }}
                variant="h6"
              >
                {subheader ?? ''}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </BannerContainer>
  );
};
